import React, { useMemo, useState } from 'react';
import { X, FileText, User, Calendar, Save, Plus, Euro, Percent } from 'lucide-react';
import { useLeads } from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';

interface CreateProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (proposalData: any) => void;
}

type PaymentPlan = 'full' | 'installments';
type CustomService = { name: string; fee: number };
type DiscountType = 'percent' | 'amount';

const CreateProposalModal: React.FC<CreateProposalModalProps> = ({ isOpen, onClose, onSave }) => {
  const { leads, loading: leadsLoading, fetchLeads } = useLeads();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    studentEmail: '',
    parentEmail: '',
    university: '',
    program: '',
    degree: 'Lisans',
    baseAmount: 0, // Temel Tutar (EUR)
    validUntil: '',
    // ek hizmet seçimleri
    includeVisa: false,
    includeAccommodation: false,
    includeInsurance: false,
    includeTranslation: false,
    includeApostille: false,
    includeAirport: false,
    includeOrientation: false,
    includeBankAccount: false,
    includePermit: false,
    includeLanguage: false,
    includeTransport: false,
    includeMentorship: false,
    includeLifeSupport: false,
    includeScholarship: false,
    // ek hizmet ücretleri
    serviceFees: {} as Record<string, number>,
    // özel hizmetler
    customServices: [] as CustomService[],
    // indirim
    discountEnabled: false,
    discountType: 'percent' as DiscountType,
    discountValue: 0, // % veya EUR
    notes: '',
    paymentPlan: 'full' as PaymentPlan
  });

  const [isSaving, setIsSaving] = useState(false);
  const [newUniversity, setNewUniversity] = useState('');
  const [newProgram, setNewProgram] = useState('');
  const [newCustomServiceName, setNewCustomServiceName] = useState('');
  const [newCustomServiceFee, setNewCustomServiceFee] = useState<number | ''>('');

  // Leads verilerini yükle
  React.useEffect(() => {
    if (isOpen && (!leads || leads.length === 0)) {
      fetchLeads();
    }
  }, [isOpen, leads]);

  // Leads verilerini formatla
  const availableLeads = React.useMemo(() => {
    return (leads || []).map(lead => ({
      id: lead.id,
      name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'İsimsiz',
      email: lead.email || '',
      phone: lead.phone || '',
      status: lead.status || 'lead',
      interestedPrograms: lead.interested_programs || [],
      interestedUniversities: lead.interested_universities || [],
      budget: lead.budget || '',
      leadScore: lead.lead_score || 0,
      priority: lead.priority || 'medium',
      contactRole: lead.contact_role || 'student',
      studentInfo: lead.student_info || {},
      guardianInfo: lead.guardian_info || {}
    }));
  }, [leads]);

  const availableUniversities = [
    'Università Bocconi',
    'Politecnico di Milano',
    'Università Cattolica del Sacro Cuore',
    'Università Statale Milano',
    'Università di Roma La Sapienza',
    'Università di Bologna',
    'Università di Padova',
    'Università di Firenze',
    'Università di Torino'
  ];

  const popularPrograms = [
    'İşletme Yönetimi','Uluslararası İşletme','İnsan Kaynakları Yönetimi','Pazarlama','Finans','Muhasebe','Ekonomi','Ekonometri','Uluslararası Ekonomi','Kamu Yönetimi','Siyaset Bilimi','Uluslararası İlişkiler',
    'Bilgisayar Mühendisliği','Elektrik-Elektronik Mühendisliği','Makine Mühendisliği','İnşaat Mühendisliği','Endüstri Mühendisliği','Yazılım Mühendisliği','Çevre Mühendisliği','Gıda Mühendisliği','Kimya Mühendisliği','Jeoloji Mühendisliği',
    'Tıp','Diş Hekimliği','Eczacılık','Hemşirelik','Fizyoterapi','Beslenme ve Diyetetik','Veteriner Hekim','Psikoloji',
    'Hukuk','Sosyoloji','Felsefe','Tarih','Coğrafya','Arkeoloji','Sanat Tarihi','Antropoloji',
    'Mimarlık','İç Mimarlık','Endüstriyel Tasarım','Grafik Tasarım','Moda Tasarımı','Güzel Sanatlar','Müzik','Sinema ve Televizyon',
    'Matematik','Fizik','Kimya','Biyoloji','İstatistik','Astronomi',
    'İletişim','Gazetecilik','Halkla İlişkiler','Radyo TV Sinema','Dijital Medya',
    'Eğitim Bilimleri','Okul Öncesi Öğretmenliği','Sınıf Öğretmenliği','İngilizce Öğretmenliği','Matematik Öğretmenliği',
    'Spor Bilimleri','Beden Eğitimi Öğretmenliği','Antrenörlük','Rekreasyon',
    'Turizm İşletmeciliği','Otel İşletmeciliği','Gastronomi','Aşçıbaşılık'
  ];

  // Ek hizmet seçenekleri
  const serviceOptions: { key: keyof typeof formData; label: string }[] = [
    { key: 'includeVisa', label: 'Vize Danışmanlığı' },
    { key: 'includeAccommodation', label: 'Konaklama Desteği' },
    { key: 'includeInsurance', label: 'Sağlık Sigortası' },
    { key: 'includeTranslation', label: 'Belge Çevirisi' },
    { key: 'includeApostille', label: 'Apostil İşlemleri' },
    { key: 'includeAirport', label: 'Havaalanı Karşılama' },
    { key: 'includeOrientation', label: 'Oryantasyon Desteği' },
    { key: 'includeBankAccount', label: 'Banka Hesabı Açma' },
    { key: 'includePermit', label: 'Oturum İzni Desteği' },
    { key: 'includeLanguage', label: 'Dil Kursu Desteği' },
    { key: 'includeTransport', label: 'Ulaşım Desteği' },
    { key: 'includeMentorship', label: 'Mentörlük Desteği' }
  ];

  const addProgram = () => {
    if (newProgram.trim() && !popularPrograms.includes(newProgram.trim())) {
      popularPrograms.push(newProgram.trim());
      setFormData(prev => ({ ...prev, program: newProgram.trim() }));
      setNewProgram('');
    }
  };

  const addUniversity = () => {
    if (newUniversity.trim() && !availableUniversities.includes(newUniversity.trim())) {
      availableUniversities.push(newUniversity.trim());
      setFormData(prev => ({ ...prev, university: newUniversity.trim() }));
      setNewUniversity('');
    }
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'EUR' }).format(n || 0);

  // Ara Toplam = temel + seçilen ek hizmet ücretleri + özel hizmet ücretleri
  const totalExtras = useMemo(() => {
    const selectedExtras = serviceOptions
      .filter(opt => (formData[opt.key] as unknown as boolean))
      .map(opt => formData.serviceFees[opt.key as string] || 0)
      .reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

    const customExtras = (formData.customServices || [])
      .map(cs => cs.fee || 0)
      .reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

    return selectedExtras + customExtras;
  }, [formData, serviceOptions]);

  const computedSubtotal = useMemo(() => {
    return (Number(formData.baseAmount) || 0) + totalExtras;
  }, [formData.baseAmount, totalExtras]);

  // İndirim hesapları
  const discountAmount = useMemo(() => {
    if (!formData.discountEnabled) return 0;
    const val = Number(formData.discountValue) || 0;
    let d =
      formData.discountType === 'percent'
        ? (computedSubtotal * Math.min(Math.max(val, 0), 100)) / 100
        : Math.max(val, 0);
    if (d > computedSubtotal) d = computedSubtotal;
    return d;
  }, [formData.discountEnabled, formData.discountType, formData.discountValue, computedSubtotal]);

  const discountedTotal = useMemo(() => computedSubtotal - discountAmount, [computedSubtotal, discountAmount]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = (e) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    if (name === 'baseAmount') {
      setFormData(prev => ({ ...prev, baseAmount: value === '' ? 0 : (parseFloat(value) || 0) }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStudentSelect = (leadId: string) => {
    const lead = availableLeads.find(s => s.id === leadId);
    if (lead) {
      let studentEmail = '';
      let parentEmail = '';

      if (lead.contactRole === 'guardian') {
        parentEmail = lead.email || '';
        studentEmail = lead.studentInfo?.email || '';
      } else {
        studentEmail = lead.email || '';
        parentEmail = lead.guardianInfo?.email || '';
      }

      setFormData(prev => ({
        ...prev,
        studentId: lead.id,
        studentName: lead.name,
        studentEmail,
        parentEmail,
        // Lead'in ilgilendiği programları ve üniversiteleri otomatik doldur
        program: lead.interestedPrograms?.[0] || '',
        university: lead.interestedUniversities?.[0] || ''
      }));
    }
  };

  const handleServiceFeeChange = (serviceKey: string, fee: number | '') => {
    setFormData(prev => ({
      ...prev,
      serviceFees: {
        ...prev.serviceFees,
        [serviceKey]: typeof fee === 'number' ? fee : 0
      }
    }));
  };

  // İndirim alanı değişimleri
  const toggleDiscount = () =>
    setFormData(prev => ({ ...prev, discountEnabled: !prev.discountEnabled }));

  const changeDiscountType: React.ChangeEventHandler<HTMLSelectElement> = (e) =>
    setFormData(prev => ({ ...prev, discountType: e.target.value as DiscountType }));

  const changeDiscountValue: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setFormData(prev => ({ ...prev, discountValue: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }));

  const addCustomService = () => {
    const name = newCustomServiceName.trim();
    const feeNum = typeof newCustomServiceFee === 'string' ? parseFloat(newCustomServiceFee) : newCustomServiceFee;
    if (!name) return;
    if (!Number.isFinite(feeNum as number)) return;

    setFormData(prev => ({
      ...prev,
      customServices: [...prev.customServices, { name, fee: Number(feeNum) }]
    }));
    setNewCustomServiceName('');
    setNewCustomServiceFee('');
  };

  const removeCustomService = (name: string) => {
    setFormData(prev => ({
      ...prev,
      customServices: prev.customServices.filter(cs => cs.name !== name)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    await new Promise(res => setTimeout(res, 700));

    onSave({
      ...formData,
      student_id: null, // Lead'ler için student_id null olacak
      subtotal: computedSubtotal,
      discountAmount,
      totalAmount: discountedTotal, // indirimli toplam kaydedilir
      consultant_name: user?.name || 'Belirtilmemiş',
      id: `proposal-${Date.now()}`,
      createdAt: new Date(),
      status: 'draft'
    });

    setIsSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Teklif Hazırla</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Öğrenci Bilgileri */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Öğrenci Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Öğrenci Seç <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="studentId"
                    value={formData.studentId}
                    onChange={(e) => handleStudentSelect(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">
                      {leadsLoading ? 'Yükleniyor...' : availableLeads.length === 0 ? 'Henüz lead yok' : 'Lead Seçin'}
                    </option>
                    {availableLeads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name} 
                        {lead.email && ` - ${lead.email}`}
                        {lead.contactRole === 'guardian' && ' (Veli)'}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.studentId && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    {(() => {
                      const selectedLead = availableLeads.find(s => s.id === formData.studentId);
                      if (!selectedLead) return null;
                      
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                              {selectedLead.contactRole === 'guardian' ? 'Veli' : 'Öğrenci'}
                            </span>
                            <span className="text-sm font-medium text-blue-800 dark:text-blue-400">
                              Lead Score: {selectedLead.leadScore}/100
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              selectedLead.priority === 'high' 
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                : selectedLead.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                              {selectedLead.priority === 'high' ? 'Yüksek' : selectedLead.priority === 'medium' ? 'Orta' : 'Düşük'} Öncelik
                            </span>
                          </div>
                          <div className="text-sm text-blue-700 dark:text-blue-300">
                            <p><strong>E-posta:</strong> {selectedLead.email}</p>
                            {selectedLead.phone && (
                              <p><strong>Telefon:</strong> {selectedLead.phone}</p>
                            )}
                            {selectedLead.budget && (
                              <p><strong>Bütçe:</strong> {selectedLead.budget}</p>
                            )}
                            {selectedLead.interestedPrograms?.length > 0 && (
                              <p><strong>İlgilendiği Programlar:</strong> {selectedLead.interestedPrograms.slice(0, 3).join(', ')}</p>
                            )}
                            {selectedLead.interestedUniversities?.length > 0 && (
                              <p><strong>İlgilendiği Üniversiteler:</strong> {selectedLead.interestedUniversities.slice(0, 2).join(', ')}</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Öğrenci E-posta <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="studentEmail"
                  value={formData.studentEmail}
                  onChange={handleChange}
                  required
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Veli E-posta
                </label>
                <input
                  type="email"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleChange}
                  placeholder="veli@email.com (opsiyonel)"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Üniversite
                </label>
                <div className="space-y-3">
                  <select
                    name="university"
                    value={formData.university}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Üniversite Seçin</option>
                    {availableUniversities.map(uni => (
                      <option key={uni} value={uni}>{uni}</option>
                    ))}
                  </select>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newUniversity}
                      onChange={(e) => setNewUniversity(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUniversity())}
                      placeholder="Özel üniversite adı girin..."
                      className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={addUniversity}
                      className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                    >
                      <Plus className="w-3 h-3 inline-block mr-1" />
                      Ekle
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Program
                </label>
                <div className="space-y-3">
                  <select
                    name="program"
                    value={formData.program}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Program Seçin</option>
                    {popularPrograms.map(program => (
                      <option key={program} value={program}>{program}</option>
                    ))}
                  </select>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newProgram}
                      onChange={(e) => setNewProgram(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addProgram())}
                      placeholder="Özel program adı girin..."
                      className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={addProgram}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                    >
                      <Plus className="w-3 h-3 inline-block mr-1" />
                      Ekle
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Derece
                </label>
                <select
                  name="degree"
                  value={formData.degree}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Lisans">Lisans</option>
                  <option value="Yüksek Lisans">Yüksek Lisans</option>
                  <option value="Doktora">Doktora</option>
                </select>
              </div>
            </div>
          </div>

          {/* Fiyat Bilgileri */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Fiyat Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Temel Tutar (EUR) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    name="baseAmount"
                    value={formData.baseAmount === 0 ? '' : formData.baseAmount}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 
                               bg-gray-50 dark:bg-gray-800 
                               border border-gray-200 dark:border-gray-700 
                               rounded-lg text-gray-900 dark:text-white 
                               placeholder-gray-400 dark:placeholder-gray-500 
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ara Toplam (EUR)
                </label>
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(computedSubtotal)}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Ara Toplam = Temel Tutar + seçilen ek hizmet ücretleri + özel hizmet ücretleri
                </p>
              </div>
            </div>

            {/* İndirim */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">İsteğe Bağlı İndirim</h4>
                <button
                  type="button"
                  onClick={toggleDiscount}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.discountEnabled
                      ? 'bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  {formData.discountEnabled ? 'İndirimi Kaldır' : 'İndirim Ekle'}
                </button>
              </div>

              {formData.discountEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tür
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={changeDiscountType}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="percent">% Yüzde</option>
                      <option value="amount">€ Tutar</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Değer
                    </label>
                    <div className="relative">
                      {formData.discountType === 'percent' ? (
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      ) : (
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      )}
                      <input
                        type="number"
                        min="0"
                        max={formData.discountType === 'percent' ? 100 : undefined}
                        step="0.01"
                        value={formData.discountValue === 0 ? '' : formData.discountValue}
                        onChange={changeDiscountValue}
                        placeholder={formData.discountType === 'percent' ? '0' : '0.00'}
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.discountType === 'percent'
                        ? '0–100 arası bir yüzde girin.'
                        : 'Toplamdan düşülecek € tutar girin.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      İndirim Tutarı
                    </label>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {formatCurrency(discountAmount)}
                    </div>
                  </div>
                </div>
              )}

              {/* İndirimli Toplam */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    İndirimli Toplam (EUR)
                  </label>
                  <div className="px-4 py-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-lg font-semibold text-green-800 dark:text-green-200">
                    {formatCurrency(discountedTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ek Hizmetler */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Ek Hizmetler</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {serviceOptions.map(({ key, label }) => {
                const checked = (formData[key] as unknown as boolean) || false;
                const fee = formData.serviceFees[key as string] || 0;

                return (
                  <div key={key as string} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
                    <label className="flex items-center gap-2">
                      <input
                        id={key as string}
                        type="checkbox"
                        name={key as string}
                        checked={checked}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                    </label>

                    <div className="relative w-40">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        disabled={!checked}
                        value={fee === 0 ? '' : fee}
                        onChange={(e) =>
                          handleServiceFeeChange(
                            key as string,
                            e.target.value === '' ? '' : parseFloat(e.target.value)
                          )
                        }
                        placeholder="0.00"
                        className={`w-full pl-9 pr-3 py-2 rounded-md border text-sm
                          ${checked
                            ? 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500'
                            : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70 cursor-not-allowed'
                          }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Özel Hizmetler */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Özel Ek Hizmetler
              </label>

              <div className="flex flex-wrap gap-2 mb-3">
                {formData.customServices.map((service) => (
                  <span
                    key={service.name}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-600/20 dark:text-purple-300 text-sm rounded-full border border-purple-200 dark:border-purple-600/30"
                  >
                    <span>{service.name}</span>
                    <span className="font-semibold">{formatCurrency(service.fee)}</span>
                    <button
                      type="button"
                      onClick={() => removeCustomService(service.name)}
                      className="hover:text-purple-600 dark:hover:text-purple-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newCustomServiceName}
                  onChange={(e) => setNewCustomServiceName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomService())}
                  placeholder="Özel hizmet adı..."
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm placeholder-gray-400 dark:placeholder-gray-500"
                />
                <div className="relative w-full sm:w-48">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={newCustomServiceFee}
                    onChange={(e) => setNewCustomServiceFee(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomService())}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={addCustomService}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Plus className="w-3 h-3 inline-block mr-1" />
                  Ekle
                </button>
              </div>
            </div>
          </div>

          {/* Ödeme & Geçerlilik */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Ödeme & Geçerlilik</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ödeme Türü
                </label>
                <select
                  name="paymentPlan"
                  value={formData.paymentPlan}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                >
                  <option value="full">Tek Ödeme</option>
                  <option value="installments">Taksitli Ödeme</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Geçerlilik Tarihi <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notlar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ek Notlar
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Teklif hakkında ek bilgiler..."
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <div><span className="font-medium">Ara Toplam Ek Hizmetler:</span> {formatCurrency(totalExtras)}</div>
              {formData.discountEnabled && (
                <div><span className="font-medium">İndirim:</span> −{formatCurrency(discountAmount)}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all ${
                  isSaving
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-purple-600/25'
                }`}
              >
                <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
                <span>{isSaving ? 'Hazırlanıyor...' : 'Teklif Hazırla'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProposalModal;
